import React, { useEffect, useState } from "react";
import { Row, Col, Spinner, Card, ProgressBar, Accordion } from "react-bootstrap";
import Plot from "react-plotly.js";

import SystemsOverviewConnection from './SystemsOverviewConnection.js';
import SystemsConnectionTrend from './SystemsConnectionTrend.js'
import SystemsConnectionByCountry from './SystemsConnectionByCountry.js';
import SystemsConnectionByCreationYear from './SystemsConnectionByCreationYear.js';
import SystemsConnectionByDeviceType from './SystemsConnectionByDeviceType.js';

import { systemsConnectionAnalysisAPI } from "../../api/api_definitions.js";


const SystemsOverviewInfo = () => {

  const [loading, setLoading] = useState(true);
  const [infoSystemsConnected, setInfoSystemsConnected] = useState(null);
  const [activeAccordion, setActiveAccordion] = useState(null);

  useEffect(() => {
    const fetchData = async (retry = 0) => {
      try {
        const dataAPI = await systemsConnectionAnalysisAPI();
        setInfoSystemsConnected(dataAPI);
      } catch (error) {
        console.error(error);

        if (retry < 3) {
          setTimeout(() => fetchData(retry + 1), 2000);
          return;
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (!infoSystemsConnected || loading) {
    return (
      <Row className="d-flex flex-column align-items-center text-center">
        <p className="mt-4">Caricamento dati...</p>
        <Spinner animation="border" />
      </Row>
    );
  }

  // -------------------------
  // PARSE DATA
  // -------------------------

  const laser = JSON.parse(infoSystemsConnected.DF_LASER);
  const automation = JSON.parse(infoSystemsConnected.DF_AUTOMATION);

  const standalone = laser.Count?.STANDALONE || 0;
  const system = laser.Count?.SYSTEM || 0;

  const deviceTypes = Object.keys(automation.Total || {});

  const configurableValues = deviceTypes.map(
    (d) => automation.Configurable?.[d] || 0
  );

  const totalValues = deviceTypes.map(
    (d) => automation.Total?.[d] || 0
  );

  const missingValues = totalValues.map(
    (t, i) => t - configurableValues[i]
  );

  const automationTotal = totalValues.reduce((a, b) => a + b, 0);
  const automationConfigured = configurableValues.reduce((a, b) => a + b, 0);

  const configuredPercent = automationTotal
    ? Math.round((automationConfigured / automationTotal) * 100)
    : 0;


  const devicesToBeConsidered = system + automationConfigured

  // -------------------------
  // STACKED BAR
  // -------------------------

  const barData = [
    {
      x: deviceTypes,
      y: configurableValues,
      name: "Configurable",
      type: "bar",
      marker: { color: "#1f77b4" }
    },
    {
      x: deviceTypes,
      y: missingValues,
      name: "Not Configurable",
      type: "bar",
      marker: { color: "#767676ff" }
    }
  ];

  const barLayout = {
    barmode: "stack",
    height: 350,
    title: "Device configuration status",
    margin: { l: 50, r: 30, t: 50, b: 40 },
    xaxis: { title: "" },
    yaxis: { title: "Total" },
    legend: { orientation: "h", x: 0.3 }
  };

  // -------------------------
  // RENDER
  // -------------------------

  return (
    <>

      {/* KPI SECTION */}

      {/* STANDALONE */}

      <Row className="text-center mt-4">
        <Col md={{ span: 5 }}>
          <Card>
            <Card.Body>
              <h6>Standalone 2D Laser</h6>
              <h2>{standalone}</h2>
            </Card.Body>
          </Card>
        </Col>
      </Row>


      {/* SYSTEM → AUTOMATION */}

      <Row className="text-center mt-3 align-items-center">

        <Col md={5}>
          <Card>
            <Card.Body>
              <h6>System 2D Laser</h6>
              <h2>{system}</h2>
            </Card.Body>
          </Card>
        </Col>

        <Col md={2} className="d-flex justify-content-center align-items-center">
          <div
            style={{
              fontSize: "60px",
              fontWeight: "bold",
              color: "#555"
            }}
          >
            →
          </div>
        </Col>

        <Col md={5}>
          <Card>
            <Card.Body>
              <h6>Automation Devices</h6>
              <h2>{automationTotal}</h2>
            </Card.Body>
          </Card>
        </Col>

      </Row>


      {/* CONFIGURATION RATE */}

      <Row className="text-center mt-4">

        <Col md={{ span: 5, offset: 7 }}>

          <h6>Configuration rate: {configuredPercent}%</h6>

          <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
            {automationConfigured}/{automationTotal}
          </div>

          <ProgressBar
            now={configuredPercent}
            label={`${configuredPercent}%`}
            variant="success"
          />

        </Col>

      </Row>


      {/* STACKED BAR */}

      <Row className="text-center mt-4">

        <Col md={{ span: 7, offset: 6 }}>
          <Plot data={barData} layout={barLayout} />
        </Col>

      </Row>

      <hr/>

      {/* OVERVIEW CONNECTION */}
      <Row className="text-center mt-5">
        <Col>

          <h4>
            Tutte le prossime analisi saranno basate su
            <strong> {devicesToBeConsidered} dispositivi</strong>
          </h4>

          <h6 className="text-muted mt-2">
            <strong>{system}</strong> macchine laser e{" "}
            <strong>{automationConfigured}</strong> dispositivi di automazione
            (solo quelli configurati)
          </h6>

        </Col>
      </Row>


      <Row className="mt-4 mb-5">

        <Col md={4}>
          <div className="p-3 border rounded text-center">
            <h6 style={{color:"#2ca02c", fontWeight:600}}>
              ACTIVE
            </h6>

            <div style={{fontSize:"13px", color:"#666"}}>
              Ha trasmesso almeno un allarme negli ultimi
              <strong> 30 giorni</strong>
            </div>
          </div>
        </Col>


        <Col md={4}>
          <div className="p-3 border rounded text-center">
            <h6 style={{color:"#ff7f0e", fontWeight:600}}>
              INACTIVE
            </h6>

            <div style={{fontSize:"13px", color:"#666"}}>
              Non ha trasmesso allarmi negli ultimi
              <strong> 30 giorni</strong> ma almeno
              una volta nella sua vita
            </div>
          </div>
        </Col>


        <Col md={4}>
          <div className="p-3 border rounded text-center">
            <h6 style={{color:"#d62728", fontWeight:600}}>
              NEVER ACTIVE
            </h6>

            <div style={{fontSize:"13px", color:"#666"}}>
              Non ha mai trasmesso
              <strong> alcun allarme</strong>
            </div>
          </div>
        </Col>

      </Row>

      <hr/>

      <Row className="text-center mt-5 mb-5">
            <SystemsOverviewConnection data={infoSystemsConnected.DF} />
      </Row>

      <hr />

      <Row className="text-center mt-5 mb-5">
            <SystemsConnectionTrend data={infoSystemsConnected.DF_TREND} />
      </Row>

      <hr />
      
      <hr style={{ borderTop: "2px solid #dee2e6" }} />

      <h5
        style={{
          fontWeight: 600,
          color: "#495057",
          marginBottom: "15px"
        }} className="text-center mt-5 mb-5"
      >
        📊 Analisi aggiuntive
      </h5>

      <Accordion
        className="mb-5"
        activeKey={activeAccordion}
        onSelect={(k) => setActiveAccordion(k)}
      >

        {/* REGION UNIT */}

        <Accordion.Item eventKey="0">

          <Accordion.Header>
            Connessioni per Region Unit
          </Accordion.Header>

          <Accordion.Body>

            {activeAccordion === "0" && (

              <Row className="text-center mt-3">
                <SystemsConnectionByCountry
                  dataCountry={infoSystemsConnected.DF_COUNTRY}
                  data={infoSystemsConnected.DF}
                />
              </Row>

            )}

          </Accordion.Body>

        </Accordion.Item>


        {/* CREATION YEAR */}

        <Accordion.Item eventKey="1">

          <Accordion.Header>
            Connessioni per Anno di Creazione
          </Accordion.Header>

          <Accordion.Body>

            {activeAccordion === "1" && (

              <Row className="text-center mt-3">
                <SystemsConnectionByCreationYear
                  data={infoSystemsConnected.DF}
                />
              </Row>

            )}

          </Accordion.Body>

        </Accordion.Item>


        {/* DEVICE TYPE */}

        <Accordion.Item eventKey="2">

          <Accordion.Header>
            Connessioni per Tipologia di dispositivo
          </Accordion.Header>

          <Accordion.Body>

            {activeAccordion === "2" && (

              <Row className="text-center mt-3">
                <SystemsConnectionByDeviceType
                  data={infoSystemsConnected.DF}
                />
              </Row>

            )}

          </Accordion.Body>

        </Accordion.Item>

      </Accordion>
    </>
  );
};

export default SystemsOverviewInfo;